import { useEffect, useState } from 'react';
import { TablePaginationConfig } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { paths } from '@app/constants';
import { showDefaultErrorToast } from '@app/utils';
import {
  useIgnoreMerge,
  useOptimisticMergeQuestionWithSuggestedQuestion,
  useSuggestedQuestions as useSuggestedQuestionsQuery,
} from '@data/hooks';
import { QuestionsApiGetSuggestedQuestionsRequest } from '@data/sdk/match';

export const useSuggestedQuestions = (threshold: number) => {
  const navigate = useNavigate();
  const { questionId = '' } = useParams<{ questionId: string }>();

  const [query, setQuery] = useState<{ limit: number; page: number }>({
    limit: 10,
    page: 1,
  });

  const { limit, page } = query;

  const params: Omit<QuestionsApiGetSuggestedQuestionsRequest, 'id' | 'threshold'> = {
    limit,
    offset: (page - 1) * limit,
    currentVersionOnly: false,
  };

  const { data: suggestedQuestions, isLoading: isSuggestedQuestionsLoading } =
    useSuggestedQuestionsQuery(questionId, {
      ...params,
      threshold,
    });

  const { mutate: ignoreMerge } = useIgnoreMerge();
  const { mutate: mergeQuestions } = useOptimisticMergeQuestionWithSuggestedQuestion();

  const handleTableChange = ({ pageSize, current }: TablePaginationConfig) => {
    setQuery({
      ...query,
      page: pageSize && pageSize > query.limit ? 1 : current ?? query.page,
      limit: pageSize ?? query.limit,
    });
  };

  const navigateToQuestionMerge = (id: string) => {
    navigate(
      `/${paths.app}/${paths.questions_library.root}/${id}/${paths.questions_library.merge}`,
    );
  };

  const handleQuestionsMerge = (id: string) => {
    mergeQuestions(
      {
        id,
        values: { targetQuestionId: questionId },
        suggestedQuestionsParams: {
          ...params,
          id: questionId,
          threshold,
        },
      },
      {
        onError: showDefaultErrorToast,
      },
    );
  };

  const handleMergeIgnore = (id: string) => {
    ignoreMerge(
      {
        id,
        secondQuestionId: questionId,
        suggestedQuestionsParams: {
          ...params,
          id: questionId,
          threshold,
        },
      },
      {
        onError: showDefaultErrorToast,
      },
    );
  };

  useEffect(() => {
    setQuery({
      ...query,
      page: 1,
    });
  }, [threshold]);

  return {
    current: query.page,
    pageSize: query.limit,
    suggestedQuestions,
    isSuggestedQuestionsLoading,
    handleTableChange,
    handleMergeIgnore,
    handleQuestionsMerge,
    navigateToQuestionMerge,
  };
};
